<script setup lang="ts">
import EventMapExportButton from './EventMapExportButton.vue'
import type { EventMapExportSnapshot } from './event-map-export.types'
import EventMapIndividualSaleIcon from '~/shared-kernel/presentation/event-map/EventMapIndividualSaleIcon.vue'
import { UTooltip } from '#components'
import { computed, nextTick, shallowRef, useTemplateRef, watch } from 'vue'
import EventMapRendererClient from '~/shared-kernel/presentation/event-map/EventMapRenderer.client.vue'
import EventMapTooltip from '~/shared-kernel/presentation/event-map/EventMapTooltip.vue'
import type { EventMapDocument, EventMapUnitDetails, EventMapUnitView } from '~/shared-kernel/presentation/event-map/event-map.types'

const props = withDefaults(defineProps<{
  document: EventMapDocument
  loadExportSnapshot?: () => Promise<EventMapExportSnapshot>
  focusedLocalityId: string | null
  inspectedUnitId: string | null
  ownedUnitIds?: string[]
  pendingUnitIds?: string[]
  ownedLocalityIds?: string[]
  pendingLocalityIds?: string[]
  unitDetails?: Record<string, EventMapUnitDetails>
  selectable?: boolean
  activatable?: boolean
  showActivationTooltip?: boolean
  localitySummaryLabels?: Record<string, string>
  // Short text under each locality chip, e.g. its price.
  localityCaptions?: Record<string, string>
  showBackToMap?: boolean
  showReferenceSections?: boolean
}>(), {
  activatable: false,
  showActivationTooltip: false,
  showBackToMap: true,
  showReferenceSections: true
})

const emit = defineEmits<{
  focusLocality: [localityId: string | null]
  inspectUnit: [unitId: string | null]
  selectUnit: [unitId: string]
  activateUnit: [unitId: string]
  selectLocality: [localityId: string]
}>()

type ViewportCommandKind = 'fit' | 'zoom-in' | 'zoom-out'
const viewportCommand = shallowRef<{ id: number; kind: ViewportCommandKind }>({ id: 0, kind: 'fit' })
const canvasFailed = shallowRef(false)
const surfaceRef = useTemplateRef<HTMLDivElement>('surface')
const localitiesRef = useTemplateRef<HTMLDivElement>('localities')
// Keeps the active chip visible in the single scrolling row; only the row scrolls, never the page.
watch(() => props.focusedLocalityId, async (localityId) => {
  await nextTick()
  const row = localitiesRef.value
  const chip = localityId ? row?.querySelector(`[data-locality-id="${CSS.escape(localityId)}"]`) : null
  if (!row || !chip) return
  const offset = chip.getBoundingClientRect().left - row.getBoundingClientRect().left
  row.scrollTo({ left: row.scrollLeft + offset - 8, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
})
const hoverPosition = shallowRef<{ x: number; y: number; halfWidth: number; halfHeight: number } | null>(null)
const inspectedLocalityId = shallowRef<string | null>(null)
const localityTooltipUi = {
  content: 'max-w-xs h-auto py-2',
  text: 'whitespace-pre-line overflow-visible text-clip break-words'
}
const inspectedLocalitySummary = computed(() => {
  const id = inspectedLocalityId.value
  const summary = id ? props.localitySummaryLabels?.[id] : undefined
  const locality = props.document.localities.find(item => item.id === id)
  return summary && locality ? `${locality.name}\n${summary}` : undefined
})
const localityTooltipReference = computed(() => {
  const point = hoverPosition.value
  if (!point) return undefined
  return {
    getBoundingClientRect: () => {
      const bounds = surfaceRef.value?.getBoundingClientRect()
      return new DOMRect((bounds?.left ?? 0) + point.x, (bounds?.top ?? 0) + point.y, 0, 0)
    }
  }
})
const renderedDocument = computed<EventMapDocument>(() => {
  if (props.showReferenceSections) return props.document

  const sections = props.document.sections.filter(section => section.localityId !== null)
  const sectionIds = new Set(sections.map(section => section.id))
  return {
    ...props.document,
    sections,
    seatLayouts: Object.fromEntries(Object.entries(props.document.seatLayouts)
      .filter(([sectionId]) => sectionIds.has(sectionId)))
  }
})

const hasIndividualSales = computed(() => renderedDocument.value.sections
  .filter(section => !props.focusedLocalityId || section.localityId === props.focusedLocalityId)
  .some(section => renderedDocument.value.seatLayouts[section.id]?.units.some(unit => unit.individualSale)))

const hasExpandedGroupSales = computed(() => renderedDocument.value.sections
  .filter(section => !props.focusedLocalityId || section.localityId === props.focusedLocalityId)
  .some(section => renderedDocument.value.seatLayouts[section.id]?.units.some(unit => unit.expandedFromGroup)))

const inspectedUnit = computed<EventMapUnitView | null>(() => {
  if (!props.inspectedUnitId) return null
  for (const layout of Object.values(renderedDocument.value.seatLayouts)) {
    const unit = layout.units.find(item => item.id === props.inspectedUnitId)
    if (unit) return unit
  }
  return null
})
const inspectedUnitDetails = computed(() => props.inspectedUnitId ? props.unitDetails?.[props.inspectedUnitId] : undefined)

// Floats the tooltip beside the hovered unit, flipping side/edge to stay inside the visible surface.
const tooltipPosition = computed(() => {
  const point = hoverPosition.value
  if (!point) return undefined
  const surfaceWidth = surfaceRef.value?.clientWidth ?? 640
  const surfaceHeight = surfaceRef.value?.clientHeight ?? 480
  const tooltipWidth = 272
  const tooltipHeight = 248
  const margin = 12

  const rightLeft = point.x + point.halfWidth + margin
  const left = rightLeft + tooltipWidth <= surfaceWidth
    ? rightLeft
    : Math.max(margin, point.x - point.halfWidth - margin - tooltipWidth)

  const aboveTop = point.y - point.halfHeight - margin - tooltipHeight
  const top = aboveTop >= 0
    ? aboveTop
    : Math.min(surfaceHeight - tooltipHeight - margin, point.y + point.halfHeight + margin)

  return {
    left: `${Math.min(Math.max(left, margin), Math.max(margin, surfaceWidth - tooltipWidth - margin))}px`,
    top: `${Math.max(margin, top)}px`
  }
})

function command(kind: ViewportCommandKind) {
  viewportCommand.value = { id: viewportCommand.value.id + 1, kind }
}
</script>

<template>
  <section class="event-map-renderer" :aria-label="$t('eventMap.title')">
    <div class="event-map-renderer__toolbar" role="group" :aria-label="$t('eventMap.controls')">
      <TqButton
        v-if="showBackToMap && focusedLocalityId"
        class="event-map-renderer__back"
        data-action="back-to-map"
        variant="secondary"
        icon="i-lucide-arrow-left"
        @click="emit('focusLocality', null)"
      >
        <span class="event-map-renderer__back-label">{{ $t('eventMap.actions.backToMap') }}</span>
      </TqButton>
      <div ref="localities" class="event-map-renderer__localities">
        <component
          :is="localitySummaryLabels?.[locality.id] ? UTooltip : 'span'"
          v-for="locality in renderedDocument.localities"
          :key="locality.id"
          v-bind="localitySummaryLabels?.[locality.id] ? {
            text: localitySummaryLabels[locality.id],
            content: { ariaLabel: localitySummaryLabels[locality.id] },
            ui: localityTooltipUi
          } : {}"
        >
          <TqButton
            class="event-map-renderer__locality"
            :data-locality-id="locality.id"
            :variant="focusedLocalityId === locality.id ? 'primary' : 'ghost'"
            @click="emit('focusLocality', locality.id)"
          >
            <span class="event-map-renderer__locality-text">
              {{ locality.name }}
              <small v-if="localityCaptions?.[locality.id]">{{ localityCaptions[locality.id] }}</small>
            </span>
          </TqButton>
        </component>
      </div>
    </div>

    <div v-if="hasIndividualSales || loadExportSnapshot" class="event-map-renderer__legend" :aria-label="$t('eventMap.legend.title')">
      <EventMapIndividualSaleIcon v-if="hasIndividualSales" />
      <span v-if="hasIndividualSales">{{ $t('eventMap.legend.individualSale') }}</span>
      <template v-if="hasExpandedGroupSales">
        <EventMapIndividualSaleIcon expanded />
        <span>{{ $t('eventMap.legend.expandedFromGroup') }}</span>
      </template>
      <span v-if="loadExportSnapshot" class="event-map-renderer__export">
        <EventMapExportButton :event-id="document.eventId" :load-snapshot="loadExportSnapshot" />
      </span>
    </div>

    <div ref="surface" class="event-map-renderer__surface">
      <!-- Floating over the map keeps the toolbar to a single row on phones. -->
      <div v-if="!canvasFailed" class="event-map-renderer__zoom">
        <TqButton variant="secondary" icon="i-lucide-zoom-in" :aria-label="$t('eventMap.actions.zoomIn')" @click="command('zoom-in')" />
        <TqButton variant="secondary" icon="i-lucide-zoom-out" :aria-label="$t('eventMap.actions.zoomOut')" @click="command('zoom-out')" />
        <TqButton variant="secondary" icon="i-lucide-scan" :aria-label="$t('eventMap.actions.fit')" @click="command('fit')" />
      </div>
      <UAlert
        v-if="canvasFailed"
        color="warning"
        variant="soft"
        :title="$t('eventMap.canvasErrorTitle')"
        :description="$t('eventMap.canvasErrorDescription')"
      />
      <ClientOnly v-else>
        <EventMapRendererClient
          :document="renderedDocument"
           :focused-locality-id="focusedLocalityId"
           :inspected-unit-id="inspectedUnitId"
           :owned-unit-ids="ownedUnitIds"
           :pending-unit-ids="pendingUnitIds"
           :owned-locality-ids="ownedLocalityIds"
           :pending-locality-ids="pendingLocalityIds"
           :selectable="selectable"
           :activatable="activatable"
           :viewport-command="viewportCommand"
          @focus-locality="emit('focusLocality', $event)"
          @inspect-locality="inspectedLocalityId = $event"
           @inspect-unit="emit('inspectUnit', $event)"
           @select-unit="emit('selectUnit', $event)"
           @activate-unit="emit('activateUnit', $event)"
           @select-locality="emit('selectLocality', $event)"
           @hover-position="hoverPosition = $event"
          @error="canvasFailed = true"
        />
        <template #fallback>
          <div class="event-map-renderer__loading" role="status">{{ $t('accessibility.loading') }}</div>
        </template>
      </ClientOnly>
      <UTooltip
        v-if="inspectedLocalitySummary"
        :open="true"
        :text="inspectedLocalitySummary"
        :reference="localityTooltipReference"
        :content="{ ariaLabel: inspectedLocalitySummary, side: 'top', sideOffset: 16, updatePositionStrategy: 'always' }"
        :ui="localityTooltipUi"
        @update:open="!$event && (inspectedLocalityId = null)"
      />
      <EventMapTooltip
        v-if="inspectedUnit && (showActivationTooltip || !activatable || inspectedUnitDetails?.attendance)"
        :unit="inspectedUnit"
        :position="tooltipPosition"
        v-bind="inspectedUnitDetails ? { details: inspectedUnitDetails } : {}"
      />
    </div>
  </section>
</template>

<style scoped>
.event-map-renderer {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto auto;
  min-width: 0;
  overflow: hidden;
  border: 1px solid color-mix(in oklch, var(--tq-color-teal) 18%, var(--tq-border));
  border-radius: var(--tq-radius-lg);
  background: var(--tq-bg-elevated);
  box-shadow: var(--tq-shadow-tight);
}

.event-map-renderer__toolbar,
.event-map-renderer__localities,
.event-map-renderer__zoom {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.event-map-renderer__toolbar {
  justify-content: space-between;
  padding: 0.65rem;
  border-bottom: 1px solid var(--tq-border);
  background: color-mix(in oklch, var(--tq-bg-elevated) 88%, var(--tq-bg-muted));
}

/* One scrollable row at every width: wrapping chips pushed the map below the fold on phones. */
.event-map-renderer__localities {
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  scroll-snap-type: x proximity;
  scrollbar-width: thin;
}

.event-map-renderer__localities > * {
  flex: none;
  scroll-snap-align: start;
}

.event-map-renderer__locality-text {
  display: grid;
  justify-items: start;
  line-height: 1.15;
  text-align: start;
}

.event-map-renderer__locality-text small {
  font-size: 0.72rem;
  font-weight: 500;
  opacity: 0.85;
}

.event-map-renderer__back { flex: none; }

.event-map-renderer__zoom {
  position: absolute;
  z-index: 2;
  top: 0.65rem;
  right: 0.65rem;
  flex-direction: column;
  gap: 0.35rem;
}

.event-map-renderer__zoom .tq-button { box-shadow: var(--tq-shadow-tight); }

.event-map-renderer__export { display: inline-flex; margin-left: auto; }

.event-map-renderer__legend {
  flex-wrap: wrap;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  color: var(--tq-text);
  font-size: 0.8125rem;
}

.event-map-renderer__surface {
  position: relative;
  min-height: 30rem;
  background:
    radial-gradient(circle at 20% 10%, color-mix(in oklch, var(--tq-color-teal) 8%, transparent), transparent 20rem),
    var(--tq-bg-muted);
}

.event-map-renderer__loading {
  display: grid;
  min-height: 30rem;
  place-items: center;
  color: var(--tq-text-soft);
}

@media (max-width: 640px) {
  .event-map-renderer__surface,
  .event-map-renderer__loading {
    min-height: 24rem;
  }

  .event-map-renderer__toolbar {
    gap: 0.4rem;
    padding: 0.5rem;
  }

  .event-map-renderer__legend {
    flex-wrap: nowrap;
    gap: 0.4rem;
    padding: 0.4rem 0.5rem;
    font-size: 0.75rem;
  }

  .event-map-renderer__export { flex: none; }

  /* Icon-only on phones; the label stays available to assistive technology. */
  .event-map-renderer__back-label {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
}
</style>
